import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PaymentsService } from '../../api/PaymentsAPI';
import { LOCATION_META } from '../../constants';
import { quoteBreakdown } from '../../utils';
import s from '../../styles/dashboard.module.css';

// The badge on the right of each option — the handling behaviour, spelled out.
const HANDLING_BADGE = {
  links_itself:    { label: 'LINKS ITSELF',    tone: 'payMethodBadgeMuted' },
  receivable:      { label: 'RECEIVABLE',      tone: 'payMethodBadgeBlue'  },
  manual:          { label: 'MANUAL',          tone: 'payMethodBadgeMuted' },
  needs_reason:    { label: 'NEEDS A REASON',  tone: 'payMethodBadgeRed'   },
  action_required: { label: 'ACTION REQUIRED', tone: 'payMethodBadgeRed'   },
};

// Exactly what each choice does to the books, so nobody has to guess.
const OUTCOME = {
  card_at_pickup: 'Stays payment due until a matching Square charge arrives, which is usually within the hour.',
  square_invoice: 'Sends the invoice and moves the car to Invoiced. It leaves receivables when the customer pays.',
  hotel_account:  'Posts to the property account and clears when next month’s remittance is allocated.',
  e_transfer:     'Recognized immediately as a non-Square payment. It cannot reconcile to a payout, so it waits for verification.',
  cash:           'Recognized immediately and logged against your name, so cash can be audited later. It waits for verification.',
  comped:         'Recorded as a comp at $0 with your reason attached. The car stays on the board and the crew still earn credit.',
  ghost:          'Stays an exception in Needs attention and in the day’s payment-due figure until money arrives.',
};

const MANUAL_NOTE = 'Recorded as a non-Square payment against this appointment. '
  + 'Revenue, tip and crew are all logged, and the entry is stamped with your name '
  + 'so cash can be audited later.';

const toMoney = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;
// Accept "368", "$368.00", "368.5" — whatever gets typed into the field.
const parseMoney = (text) => {
  const n = parseFloat(String(text ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? Math.round(n * 100) : 0;
};

export function SetPaymentMethodModal({ appt, onClose, onSaved }) {
  const { subtotalCents, taxRate, taxCents, dueCents } = quoteBreakdown(appt);

  const [selected, setSelected] = useState(null);
  const [amountText, setAmountText] = useState(toMoney(dueCents));
  const [tipText, setTipText] = useState('$0.00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // One key per modal opening: a retried submit is the same payment, a reopened
  // modal is a new one.
  const idempotencyKey = useRef(
    (globalThis.crypto?.randomUUID?.() || `k-${Date.now()}-${Math.random().toString(16).slice(2)}`),
  ).current;

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const { data } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => PaymentsService.methods(),
    staleTime: 60 * 60 * 1000,
  });
  const methods = useMemo(() => data?.methods || [], [data]);
  const chosen = methods.find(m => m.code === selected) || null;

  const isManual = chosen?.method === 'manual';
  const needsReason = chosen?.method === 'needs_reason' || chosen?.method === 'action_required';
  const reasonMissing = needsReason && !reason.trim();
  const canSubmit = !!chosen && !reasonMissing && !saving;

  const services = appt.services?.length ? appt.services : [appt.service].filter(Boolean);
  const locName = LOCATION_META[appt.location]?.name || appt.location;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError('');
    try {
      const result = await PaymentsService.recordManual({
        bookId: appt.externalId || appt.id,
        method: chosen.code,
        amount: isManual ? parseMoney(amountText) : undefined,
        tip: isManual ? parseMoney(tipText) : undefined,
        reason: needsReason ? reason.trim() : undefined,
        idempotencyKey,
      });
      onSaved?.(result, chosen);
      onClose();
    } catch (err) {
      setError(err.message || 'Could not save the payment');
      setSaving(false);
    }
  };

  return (
    <>
      <div className={s.drawerScrim} onClick={saving ? undefined : onClose} />
      <aside className={s.drawer} role="dialog" aria-label={`Set payment method for ${appt.customer}`}>
        <div className={s.drawerHead}>
          <div>
            <div className={s.drawerEyebrow}>Set payment method</div>
            <div className={s.drawerTitle}>{appt.customer}</div>
            <div className={s.drawerSub}>{[locName, services.join(' + ')].filter(Boolean).join(' · ')}</div>
          </div>
          <button className={s.drawerClose} onClick={onClose} disabled={saving} aria-label="Close">×</button>
        </div>

        <div className={s.drawerBody}>
          <div className={s.drawerCard}>
            <div className={s.drawerCardLabel}>What happens next</div>
            <div className={s.drawerNote}>
              {chosen ? OUTCOME[chosen.code] || chosen.description
                      : 'Pick one above and this will say exactly what it does to the books.'}
            </div>

            {error && <div className={s.nextStepError}>{error}</div>}

            <div className={s.drawerActions}>
              <button className={s.drawerPrimary} onClick={handleSubmit} disabled={!canSubmit}>
                {saving ? 'Saving…'
                  : chosen ? `Set as ${chosen.title.toLowerCase()}`
                  : 'Choose a payment method'}
              </button>
              <button className={s.drawerSecondary} onClick={onClose} disabled={saving}>Cancel</button>
            </div>

            {reasonMissing && (
              <div className={s.drawerNote}>A reason is required for {chosen.title.toLowerCase()}.</div>
            )}
          </div>

          <div className={s.drawerCard}>
            <div className={s.drawerCardLabel}>How is this car being paid?</div>
            <div className={s.payMethodList}>
              {methods.map(m => {
                const badge = HANDLING_BADGE[m.method] || { label: (m.method || '').toUpperCase(), tone: 'payMethodBadgeMuted' };
                return (
                  <button
                    key={m.code}
                    type="button"
                    className={`${s.payMethodOption} ${selected === m.code ? s.payMethodSelected : ''}`}
                    onClick={() => { setSelected(m.code); setError(''); }}
                    disabled={saving}
                  >
                    <span className={s.payMethodText}>
                      <span className={s.payMethodTitle}>{m.title}</span>
                      <span className={s.payMethodDesc}>{m.description}</span>
                    </span>
                    <span className={`${s.payMethodBadge} ${s[badge.tone]}`}>{badge.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {isManual && (
            <div className={`${s.drawerCard} ${s.drawerCardSunken}`}>
              <div className={s.payFieldRow}>
                <label className={s.payField}>
                  <span className={s.drawerCardLabel}>Amount received</span>
                  <input className={s.payInput} value={amountText} disabled={saving}
                         onChange={e => setAmountText(e.target.value)}
                         onBlur={() => setAmountText(toMoney(parseMoney(amountText)))} />
                </label>
                <label className={s.payField}>
                  <span className={s.drawerCardLabel}>Tip</span>
                  <input className={s.payInput} value={tipText} disabled={saving}
                         onChange={e => setTipText(e.target.value)}
                         onBlur={() => setTipText(toMoney(parseMoney(tipText)))} />
                </label>
              </div>
              <div className={s.payBreakdown}>
                <div className={s.payBreakdownRow}>
                  <span>Subtotal</span><span>{toMoney(subtotalCents)}</span>
                </div>
                <div className={s.payBreakdownRow}>
                  <span>Tax{taxRate != null ? ` (${(taxRate * 100).toFixed(0)}%)` : ''}</span>
                  <span>{taxCents == null ? '—' : toMoney(taxCents)}</span>
                </div>
                <div className={`${s.payBreakdownRow} ${s.payBreakdownTotal}`}>
                  <span>Amount due</span><span>{toMoney(dueCents)}</span>
                </div>
                {parseMoney(amountText) !== dueCents && (
                  <div className={s.payBreakdownRow}>
                    <span className={s.payBreakdownFlag}>
                      {parseMoney(amountText) > dueCents ? 'Over' : 'Short'} by
                    </span>
                    <span className={s.payBreakdownFlag}>
                      {toMoney(Math.abs(parseMoney(amountText) - dueCents))}
                    </span>
                  </div>
                )}
              </div>

              <div className={s.drawerNote}>{MANUAL_NOTE}</div>
            </div>
          )}

          {needsReason && (
            <div className={s.drawerCard}>
              <div className={s.drawerCardLabel}>
                {chosen.code === 'ghost' ? 'What happened?' : 'Reason'}
              </div>
              <textarea className={s.payTextarea} rows={3} value={reason} disabled={saving}
                placeholder={chosen.code === 'ghost'
                  ? 'Why was the car released without payment?'
                  : 'Why is this free of charge?'}
                onChange={e => setReason(e.target.value)} />
            </div>
          )}


        </div>
      </aside>
    </>
  );
}
